import React, { useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";
import { cachePosMasterData, getCachedPosMasterData } from "../utils/posMasterDataCache";
import {
  enqueueCategoryMutation,
  persistCategoryCaches,
} from "../utils/productSyncQueue";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  createOfflineCategory,
  deleteOfflineCategory,
  hasOfflineCategoryStore,
  listOfflineCategories,
  updateOfflineCategory,
} from "../offline/categoryStore";

const BRAND = "var(--app-primary)";
const DESKTOP_CACHE_HEADER = "x-billsarthi-desktop-cache";
const AddCategory = () => {
  const [categoryList, setCategoryList] = useState([]);
  const [categoryName, setCategoryName] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  // edit state
  const [selectedId, setSelectedId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState(1); // 1=active,0=inactive

  const [confirmId, setConfirmId] = useState(null);
  const [toast, setToast] = useState(null); // {type:'success'|'error'|'info', msg:string}

  const userData = JSON.parse(localStorage.getItem("userData"))?.data;
  const restaurantid = userData?.id;

  // -------- Helpers --------
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  const isDesktopOffline = () =>
    Boolean(window.desktopOfflineMutations || window.desktopProductQueue) &&
    (isFullOfflineModeEnabled() || navigator.onLine === false);

  const applyOfflineCategories = (categories) => {
    const records = Array.isArray(categories) ? categories : [];
    setCategoryList(records);
    try { localStorage.setItem("categorylistx", JSON.stringify(records)); } catch {}
  };

  const saveCategoryLocally = async (operation, fields) => {
    const localOnly = isFullOfflineModeEnabled();
    if (localOnly) {
      if (!hasOfflineCategoryStore()) {
        throw new Error("Restart the desktop app to initialize offline category storage.");
      }
      let result;
      if (operation === "category-create") {
        result = await createOfflineCategory(restaurantid, fields.name);
      } else if (operation === "category-update") {
        result = await updateOfflineCategory(
          restaurantid,
          fields.productcategoryid,
          fields.productcategoryname,
          fields.is_active
        );
      } else {
        result = await deleteOfflineCategory(restaurantid, fields.productcategoryid);
      }
      applyOfflineCategories(result?.categories);
      return true;
    }
    if (!window.desktopOfflineMutations && !window.desktopProductQueue) return false;
    const endpoint = operation === "category-create"
      ? API_ENDPOINTS.ADD_PRODUCT_CATEGORY
      : operation === "category-update"
        ? `${API_BASE_URL}/updateproductcategory`
        : `${API_BASE_URL}/deleteproductcategory`;
    const mutation = await enqueueCategoryMutation({
      restaurantId: restaurantid,
      operation,
      endpoint,
      fields,
    });

    let next = categoryList;
    if (operation === "category-create") {
      next = [{
        id: mutation.localCategoryId,
        productcategoryname: fields.name,
        is_active: 1,
        __offlinePending: true,
        __offlineMutationId: mutation.id,
      }, ...categoryList];
    } else if (operation === "category-update") {
      next = categoryList.map((category) =>
        String(category?.id) === String(fields.productcategoryid)
          ? {
              ...category,
              productcategoryname: fields.productcategoryname,
              is_active: fields.is_active,
              __offlinePending: true,
              __offlineMutationId: mutation.id,
            }
          : category
      );
    } else if (operation === "category-delete") {
      next = categoryList.filter((category) =>
        String(category?.id) !== String(fields.productcategoryid));
    }
    setCategoryList(next);
    await persistCategoryCaches(restaurantid, next);
    return true;
  };

  // debounce search text
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedQuery(searchQuery.trim().toLowerCase()),
      200
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const filteredList = useMemo(() => {
    if (!debouncedQuery) return categoryList;
    return (categoryList || []).filter((item) =>
      String(item.productcategoryname || "")
        .toLowerCase()
        .includes(debouncedQuery)
    );
  }, [categoryList, debouncedQuery]);

  // -------- Effects --------
  useEffect(() => {
    fetchCategoriesMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- API --------
  const fetchCategories = async () => {
    if (!restaurantid) {
      showToast("error", "Restaurant not found. Please login again.");
      setLoading(false);
      return;
    }
    if (isFullOfflineModeEnabled()) {
      try {
        setLoading(true);
        const records = await listOfflineCategories(restaurantid);
        applyOfflineCategories(records);
      } catch (error) {
        showToast("error", error?.message || "Unable to load Full Offline categories.");
      } finally {
        setLoading(false);
      }
      return;
    }
    const cached = await getCachedPosMasterData(restaurantid, "categories_master");
    const hasCachedCategories = Array.isArray(cached?.records) && cached.records.length > 0;
    if (hasCachedCategories) {
      setCategoryList(cached.records);
      setLoading(false);
    }

    try {
      if (!hasCachedCategories) setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/productcategorylistformaster`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load categories");
      if (
        res.headers.get(DESKTOP_CACHE_HEADER) === "1" &&
        cached?.records?.some((category) => category?.__offlinePending)
      ) {
        setCategoryList(cached.records);
        return;
      }
      const list = Array.isArray(data?.list) ? data.list : [];
      setCategoryList(list);
      if (Array.isArray(data?.list)) {
        await cachePosMasterData(restaurantid, "categories_master", list);
      }
    } catch (e) {
      if (cached?.records?.length) {
        showToast("info", "Offline mode — showing saved categories.");
      } else {
        showToast("error", e?.message || "Failed to load categories");
      }
    } finally {
      setLoading(false);
    }
  };
  const fetchCategoriesMaster = fetchCategories;

  // ADD (exactly mobile jaise – sirf name, koi image nahi)
  const addCategory = async () => {
    if (!categoryName.trim()) {
      showToast("error", "Category name is required");
      return;
    }
    if (!restaurantid) {
      showToast("error", "Restaurant not found. Please login again.");
      return;
    }
    try {
      setSubmitting(true);

      const fields = { restaurantid, name: categoryName.trim() };
      if (isDesktopOffline()) {
        await saveCategoryLocally("category-create", fields);
        showToast("success", isFullOfflineModeEnabled()
          ? "Category saved permanently in Full Offline Mode"
          : "Category saved offline; pending sync");
        setCategoryName("");
        return;
      }

      const res = await fetch(
        API_ENDPOINTS.ADD_PRODUCT_CATEGORY,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Add failed");

      showToast("success", "Category added");
      setCategoryName("");
      // mobile code: add ke baad master list
      fetchCategoriesMaster();
    } catch (e) {
      const queued = /fetch|network/i.test(String(e?.message || ""))
        ? await saveCategoryLocally("category-create", { restaurantid, name: categoryName.trim() })
        : false;
      if (queued) {
        showToast("success", "Category saved offline");
        setCategoryName("");
      } else {
        showToast("error", e?.message || "Something went wrong");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // UPDATE (mobile jaisa /updateproductcategory)
  const updateCategory = async () => {
    const name = editName.trim();
    if (name.length < 2) {
      showToast("info", "Category name is too short");
      return;
    }
    if (!restaurantid || !selectedId) {
      showToast("error", "Something went wrong. Try again.");
      return;
    }
    try {
      setSubmitting(true);
      const fields = {
        restaurantid,
        productcategoryname: name,
        productcategoryid: selectedId,
        is_active: editStatus ? 1 : 0,
      };
      if (isDesktopOffline()) {
        await saveCategoryLocally("category-update", fields);
        showToast("success", isFullOfflineModeEnabled()
          ? "Full Offline category updated"
          : "Category update saved offline; pending sync");
        setSelectedId(null);
        setEditName("");
        setEditStatus(1);
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/updateproductcategory`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Update failed");

      showToast("success", "Category updated");
      setSelectedId(null);
      setEditName("");
      setEditStatus(1);
      // mobile code: update ke baad master list
      fetchCategoriesMaster();
    } catch (e) {
      const queued = /fetch|network/i.test(String(e?.message || ""))
        ? await saveCategoryLocally("category-update", {
            restaurantid,
            productcategoryname: name,
            productcategoryid: selectedId,
            is_active: editStatus ? 1 : 0,
          })
        : false;
      if (queued) {
        showToast("success", "Category update saved offline");
        setSelectedId(null);
        setEditName("");
        setEditStatus(1);
      } else {
        showToast("error", e?.message || "Failed to update");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!restaurantid) {
      showToast("error", "Restaurant not found. Please login again.");
      return;
    }
    try {
      const fields = { restaurantid, productcategoryid: id };
      if (isDesktopOffline()) {
        await saveCategoryLocally("category-delete", fields);
        showToast("success", isFullOfflineModeEnabled()
          ? "Category deleted from the Full Offline database"
          : "Category deleted offline; pending sync");
        setConfirmId(null);
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/deleteproductcategory`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Delete failed");
      showToast("success", "Category deleted");
      setConfirmId(null);
      fetchCategoriesMaster();
    } catch (e) {
      const queued = /fetch|network/i.test(String(e?.message || ""))
        ? await saveCategoryLocally("category-delete", { restaurantid, productcategoryid: id })
        : false;
      if (queued) {
        showToast("success", isFullOfflineModeEnabled()
          ? "Category deleted from the Full Offline database"
          : "Category deleted offline; pending sync");
        setConfirmId(null);
      } else {
        showToast("error", e?.message || "Delete failed");
      }
    }
  };

  // -------- Handlers --------
  const openEdit = (item) => {
    setSelectedId(item?.id);
    setEditName(item?.productcategoryname || "");
    setEditStatus(item?.is_active === 1 ? 1 : 0);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    if (submitting) return;
    setSelectedId(null);
    setEditName("");
    setEditStatus(1);
  };

  // ---------------------------------------------------------------------------
  return (
    <div style={sx.page}>
      <div style={sx.topBar}>
        <div>
          <div style={sx.title}>Product Categories</div>
          <div style={sx.subtitle}>Organize your menu like a pro</div>
        </div>
        <div style={sx.topActions}>
          <div style={sx.countPill}>
            Total: <b>{categoryList?.length || 0}</b>
          </div>
          <button onClick={fetchCategories} style={sx.secondaryBtn}>
            Refresh
          </button>
        </div>
      </div>

      <div style={sx.addPanel}>
        <div style={sx.addCopy}>
          <div style={sx.addTitle}>{selectedId ? "Edit Category" : "Add Category"}</div>
          <div style={sx.addHint}>
            {selectedId ? "Update the selected category details." : "Create a category for organizing menu products."}
          </div>
        </div>
        <div style={sx.addControls}>
          <input
            aria-label="Category name"
            placeholder="Enter category name"
            value={selectedId ? editName : categoryName}
            onChange={(e) => selectedId ? setEditName(e.target.value) : setCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitting) selectedId ? updateCategory() : addCategory();
            }}
            maxLength={30}
            style={sx.categoryInput}
          />
          <button
            onClick={selectedId ? updateCategory : addCategory}
            disabled={submitting || !(selectedId ? editName : categoryName).trim()}
            style={{
              ...sx.saveCategoryBtn,
              opacity: submitting || !(selectedId ? editName : categoryName).trim() ? 0.55 : 1,
              cursor: submitting || !(selectedId ? editName : categoryName).trim() ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? (selectedId ? "Updating…" : "Saving…") : (selectedId ? "Update Category" : "Save Category")}
          </button>
          {selectedId ? (
            <>
              <button
                type="button"
                onClick={() => setEditStatus((prev) => prev === 1 ? 0 : 1)}
                style={{ ...sx.statusEditBtn, ...(editStatus === 1 ? sx.statusEditActive : sx.statusEditInactive) }}
              >
                {editStatus === 1 ? "Active" : "Inactive"}
              </button>
              <button type="button" onClick={cancelEdit} style={sx.cancelEditBtn}>Cancel</button>
            </>
          ) : null}
        </div>
      </div>

      <div style={sx.toolsBar}>
        <div>
          <div style={sx.listTitle}>Category List</div>
          <div style={sx.listSubtitle}>{filteredList.length} categories shown</div>
        </div>
        <input
          placeholder="Search category name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={sx.searchInput}
        />
      </div>

      {/* Grid */}
      <div style={sx.grid}>
        {loading ? (
          <div style={sx.loaderBox}>Loading…</div>
        ) : filteredList.length === 0 ? (
          <div style={sx.emptyBox}>No categories found.</div>
        ) : (
          filteredList.map((item) => (
            <div
              key={item.id}
              style={{
                ...sx.card,
                borderColor:
                  item.is_active === 1
                    ? "rgba(var(--app-primary-rgb), .2)"
                    : "rgba(239,68,68,.2)",
              }}
            >
              <div style={sx.cardTop}>
                <div style={sx.meta}>
                  <div style={sx.name} title={item.productcategoryname}>
                    {item.productcategoryname}
                  </div>
                </div>
                <div
                  style={{
                    ...sx.badge,
                    backgroundColor:
                      item.is_active === 1 ? "#E6F4EA" : "#FEE2E2",
                    color: item.is_active === 1 ? "#166534" : "#B91C1C",
                    borderColor:
                      item.is_active === 1 ? "#B7E1C1" : "#FECACA",
                  }}
                >
                  {item.is_active === 1 ? "Active" : "Inactive"}
                </div>
              </div>

              <div style={sx.cardActions}>
                <button style={sx.smallBtn} onClick={() => openEdit(item)}>
                  Edit
                </button>
                <button
                  style={{
                    ...sx.smallBtn,
                    background: "#FEF2F2",
                    color: "#B91C1C",
                    border: "1px solid #FECACA",
                  }}
                  onClick={() => setConfirmId(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Confirm delete modal */}
      {confirmId !== null && (
        <div style={sx.modalOverlay}>
          <div style={sx.modal}>
            <div style={sx.modalTitle}>Delete Category</div>
            <div style={sx.modalText}>
              This action cannot be undone. Continue?
            </div>
            <div style={sx.modalRow}>
              <button
                style={sx.cancelBtn}
                onClick={() => setConfirmId(null)}
              >
                Cancel
              </button>
              <button
                style={sx.dangerBtn}
                onClick={() => deleteCategory(confirmId)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div style={sx.loadingOverlay}>
          <div style={sx.spinner} />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            ...sx.toast,
            background:
              toast.type === "success"
                ? "#16a34a"
                : toast.type === "error"
                ? "#b91c1c"
                : "var(--app-primary-hover)",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AddCategory;

/* ==================== styles ==================== */
const sx = {
  page: {
    minHeight: "100vh",
    background: "#F6F8FB",
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
    padding: "18px",
  },

  /* Top bar */
  topBar: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    background: "#fff",
    borderRadius: 16,
    padding: "14px 16px",
    border: "1px solid #E5E7EB",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 900, lineHeight: 1.1, color: "#0F172A" },
  subtitle: { fontSize: 12, color: "#64748B", marginTop: 4 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  countPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: "#F1F5F9",
    border: "1px solid #E2E8F0",
    fontWeight: 800,
    color: "#0F172A",
  },
  secondaryBtn: {
    background: "#F1F5F9",
    color: "#0F172A",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #E2E8F0",
    fontWeight: 800,
    cursor: "pointer",
  },
  primaryBtn: {
    background: BRAND,
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(var(--app-primary-rgb), 0.2)",
  },

  addPanel: {
    marginTop: 14,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    padding: "16px",
    borderRadius: 16,
    background: "#fff",
    border: "1px solid #E5E7EB",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
  },
  addCopy: { minWidth: 190 },
  addTitle: { fontSize: 16, fontWeight: 900, color: "#0F172A" },
  addHint: { marginTop: 4, fontSize: 12, color: "#64748B" },
  addControls: {
    display: "flex",
    alignItems: "stretch",
    gap: 10,
    flex: "1 1 520px",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  categoryInput: {
    flex: "1 1 280px",
    minWidth: 0,
    border: "1px solid #D7DEE8",
    outline: "none",
    background: "#fff",
    color: "#0F172A",
    fontSize: 14,
    padding: "11px 13px",
    borderRadius: 12,
  },
  saveCategoryBtn: {
    minWidth: 150,
    padding: "11px 16px",
    borderRadius: 12,
    border: 0,
    background: BRAND,
    color: "#fff",
    fontWeight: 900,
  },
  statusEditBtn: {
    minWidth: 92,
    padding: "11px 14px",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  statusEditActive: {
    background: "#E6F4EA",
    color: "#166534",
    border: "1px solid #86C995",
  },
  statusEditInactive: {
    background: "#FEE2E2",
    color: "#B91C1C",
    border: "1px solid #FCA5A5",
  },
  cancelEditBtn: {
    padding: "11px 14px",
    borderRadius: 12,
    background: "#F1F5F9",
    color: "#334155",
    border: "1px solid #CBD5E1",
    fontWeight: 900,
    cursor: "pointer",
  },

  /* Tools */
  toolsBar: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  listTitle: { fontSize: 15, fontWeight: 900, color: "#0F172A" },
  listSubtitle: { marginTop: 2, fontSize: 11, color: "#64748B" },
  searchInput: {
    border: "1px solid #E5E7EB",
    outline: "none",
    background: "#fff",
    width: "100%",
    maxWidth: 460,
    color: "#0F172A",
    fontSize: 14,
    padding: "12px 14px",
    borderRadius: 12,
    boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
  },

  /* Grid */
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
    marginTop: 14,
  },
  loaderBox: {
    background: "#fff",
    padding: 18,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    textAlign: "center",
    color: "#334155",
    fontWeight: 700,
  },
  emptyBox: {
    background: "#fff",
    padding: 18,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    textAlign: "center",
    color: "#64748B",
    fontWeight: 700,
  },

  card: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid rgba(17,24,39,0.06)",
    padding: 12,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
  },
  meta: { flex: 1, minWidth: 0 },
  name: {
    fontWeight: 900,
    color: "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  badge: {
    marginTop: 4,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid transparent",
  },
  cardActions: {
    marginTop: 0,
    display: "flex",
    gap: 8,
    width: "min(260px, 100%)",
  },
  smallBtn: {
    flex: 1,
    borderRadius: 10,
    padding: "8px 10px",
    border: "1px solid #E5E7EB",
    background: "#F8FAFC",
    fontWeight: 800,
    cursor: "pointer",
  },

  /* Bottom sheet */
  sheetOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.35)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 50,
  },
  sheet: {
    background: "#fff",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: 16,
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 -6px 24px rgba(0,0,0,.1)",
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  grabber: {
    width: 44,
    height: 4,
    background: "#CBD5E1",
    borderRadius: 99,
    margin: "0 auto 10px",
  },
  sheetHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  sheetTitle: { fontSize: 16, fontWeight: 900, color: "#0F172A" },
  iconBtn: {
    background: "#F1F5F9",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "6px 10px",
    cursor: "pointer",
  },

  formRow: { marginTop: 12 },
  label: {
    fontWeight: 700,
    color: "#0F172A",
    marginBottom: 6,
    display: "block",
  },
  input: {
    width: "100%",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#F8FAFC",
    color: "#0F172A",
    outline: "none",
  },
  hint: { fontSize: 12, color: "#64748B", marginTop: 6 },

  primaryBtnFull: {
    marginTop: 16,
    width: "100%",
    height: 48,
    borderRadius: 14,
    background: BRAND,
    color: "#fff",
    fontWeight: 900,
    border: "none",
    cursor: "pointer",
  },

  // status row for edit sheet
  statusRow: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 12,
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  statusLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
  },
  statusBadge: {
    padding: "4px 9px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontSize: 12,
    fontWeight: 700,
  },
  toggleWrap: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
  },
  toggleTrack: {
    width: 42,
    height: 22,
    borderRadius: 999,
    background: "#E5E7EB",
    position: "relative",
    transition: "background 0.2s ease",
  },
  toggleThumb: {
    position: "absolute",
    top: 3,
    left: 3,
    width: 16,
    height: 16,
    borderRadius: "999px",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,.2)",
    transition: "transform 0.2s ease",
  },

  /* Confirm Modal */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 60,
  },
  modal: {
    width: 360,
    maxWidth: "92vw",
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,.2)",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#0F172A",
    textAlign: "center",
  },
  modalText: {
    color: "#475569",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 12,
  },
  modalRow: {
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
  },
  cancelBtn: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    background: "#F8FAFC",
    border: "1px solid #E5E7EB",
    fontWeight: 800,
    cursor: "pointer",
  },
  dangerBtn: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    color: "#B91C1C",
    fontWeight: 900,
    cursor: "pointer",
  },

  /* Loading overlay */
  loadingOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(255,255,255,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 40,
  },
  spinner: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "3px solid #cfe6f5",
    borderTopColor: BRAND,
    animation: "spin 1s linear infinite",
  },

  /* Toast */
  toast: {
    position: "fixed",
    bottom: 22,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 12,
    fontWeight: 800,
    boxShadow: "0 10px 20px rgba(0,0,0,.2)",
    zIndex: 70,
  },
};
